import { api } from './api'
import type { User, UserRole } from '@/types'

// Interface para usuário com permissões
export interface UserWithPermissions extends User {
  role: UserRole | UserRole[] // Suporte a múltiplos roles
  email?: string
  telefone?: string
  isActive?: boolean
  lastLogin?: string
  createdAt?: string
  is_admin?: boolean
}

// Interface para criar/editar usuário
export interface CreateUserData {
  login: string
  nome: string
  senha: string
  role: UserRole | UserRole[] // Suporte a múltiplos roles
  email?: string
  telefone?: string
}

export interface UpdateUserData {
  nome?: string
  role?: UserRole | UserRole[] // Suporte a múltiplos roles
  email?: string
  telefone?: string
  isActive?: boolean
}

// Interface para resposta da API
export interface UsersResponse {
  success: boolean
  data: UserWithPermissions[]
  total: number
  message?: string
}

export interface UserResponse {
  success: boolean
  data: UserWithPermissions
  message?: string
}

// Serviço de gerenciamento de usuários
export const userManagementService = {
  // Listar todos os usuários
  async getUsers(): Promise<UsersResponse> {
    try {
      // Primeiro, tentar buscar da API
      const apiResponse = await api.get('/get-all-user-local')
      
      // Verificar tanto 'success' quanto 'sucess' (erro de digitação no backend)
      const isSuccess = apiResponse.data && (apiResponse.data.success || apiResponse.data.sucess)
      
      if (isSuccess) {
        // Converter dados da API para o formato esperado
        // Os dados podem estar em 'data' ou 'user' (dependendo da rota)
        const apiUsers = apiResponse.data.data || apiResponse.data.user || []
        const formattedUsers: UserWithPermissions[] = apiUsers.map((user: any) => ({
          id: user.id,
          login: user.login,
          nome: user.nome || user.login, // Usar login como nome se nome não estiver disponível
          role: user.is_admin ? 'administrador' : (user.role || 'usuario_basico'),
          email: user.email,
          telefone: user.telefone,
          isActive: user.isActive !== false, // Default true se não especificado
          createdAt: user.created_at,
          lastLogin: user.lastLogin,
          is_admin: user.is_admin
        }))
        
        return {
          success: true,
          data: formattedUsers,
          total: formattedUsers.length,
          message: 'Usuários carregados da API'
        }
      }
      
      // Fallback para dados locais se API não funcionar
      console.warn('⚠️ API não retornou success=true, usando dados locais')
      const users = this.getLocalUsers()
      return {
        success: true,
        data: users,
        total: users.length,
        message: 'Usuários carregados do localStorage (fallback)'
      }
    } catch (error) {
      console.error('❌ Erro ao buscar usuários da API:', error)
      console.error('🔍 Detalhes do erro:', {
        message: (error as any).message,
        status: (error as any).response?.status,
        data: (error as any).response?.data,
        url: (error as any).config?.url
      })
      
      // Fallback para dados locais em caso de erro
      try {
        const users = this.getLocalUsers()
        return {
          success: true,
          data: users,
          total: users.length,
          message: 'Usuários carregados do localStorage (erro na API)'
        }
      } catch (localError) {
        console.error('❌ Erro ao buscar usuários locais:', localError)
        return {
          success: false,
          data: [],
          total: 0,
          message: 'Erro ao buscar usuários'
        }
      }
    }
  },

  // Buscar usuário por ID
  async getUserById(id: number): Promise<UserResponse> {
    try {
      // Primeiro, tentar buscar da API
      const apiResponse = await api.get(`/get-user-local/${id}`)
      
      if (apiResponse.data && (apiResponse.data.success || apiResponse.data.sucess)) {
        const apiUser = apiResponse.data.data || apiResponse.data.user
        
        const formattedUser: UserWithPermissions = {
          id: apiUser.id,
          login: apiUser.login,
          nome: apiUser.nome || apiUser.login, // Usar login como fallback se nome não existir
          role: apiUser.is_admin ? 'administrador' : (apiUser.role || 'usuario_basico'),
          is_admin: apiUser.is_admin,
          email: apiUser.email,
          telefone: apiUser.telefone,
          isActive: apiUser.isActive !== false,
          createdAt: apiUser.created_at || apiUser.createdAt,
          lastLogin: apiUser.lastLogin
        }
        
        return {
          success: true,
          data: formattedUser,
          message: 'Usuário carregado da API'
        }
      }
      
      // Fallback para dados locais
      const users = this.getLocalUsers()
      const user = users.find(u => u.id === id)
      
      if (!user) {
        return {
          success: false,
          data: {} as UserWithPermissions,
          message: 'Usuário não encontrado'
        }
      }
      
      return {
        success: true,
        data: user,
        message: 'Usuário carregado do localStorage (fallback)'
      }
    } catch (error) {
      console.error('Erro ao buscar usuário:', error)
      
      // Fallback para dados locais em caso de erro
      try {
        const users = this.getLocalUsers()
        const user = users.find(u => u.id === id)
        
        if (!user) {
          return {
            success: false,
            data: {} as UserWithPermissions,
            message: 'Usuário não encontrado'
          }
        }

        return {
          success: true,
          data: user,
          message: 'Usuário carregado do localStorage (erro na API)'
        }
      } catch (localError) {
        return {
          success: false,
          data: {} as UserWithPermissions,
          message: 'Erro ao buscar usuário'
        }
      }
    }
  },

  // Criar novo usuário
  async createUser(userData: CreateUserData): Promise<UserResponse> {
    try {
      // Primeiro, tentar criar via API
      const apiResponse = await api.post('/create-user-local', userData)
      
      if (apiResponse.data && apiResponse.data.success) {
        const apiUser = apiResponse.data.data
        const formattedUser: UserWithPermissions = {
          id: apiUser.id,
          login: apiUser.login,
          nome: apiUser.nome,
          role: apiUser.is_admin ? 'administrador' : (apiUser.role || 'usuario_basico'),
          is_admin: apiUser.is_admin,
          email: apiUser.email,
          telefone: apiUser.telefone,
          isActive: apiUser.isActive !== false,
          createdAt: apiUser.createdAt,
          lastLogin: apiUser.lastLogin
        }
        
        return {
          success: true,
          data: formattedUser,
          message: 'Usuário criado com sucesso na API'
        }
      }
      
      // Fallback para criação local
      console.warn('API não disponível, criando usuário localmente')
      const users = this.getLocalUsers()
      const newUser: UserWithPermissions = {
        id: Date.now(), // ID temporário
        login: userData.login,
        nome: userData.nome,
        role: userData.role,
        email: userData.email,
        telefone: userData.telefone,
        isActive: true,
        createdAt: new Date().toISOString()
      }

      users.push(newUser)
      this.saveLocalUsers(users)

      return {
        success: true,
        data: newUser,
        message: 'Usuário criado localmente (API não disponível)'
      }
    } catch (error) {
      console.error('Erro ao criar usuário na API:', error)
      
      // Fallback para criação local em caso de erro
      try {
        const users = this.getLocalUsers()
        const newUser: UserWithPermissions = {
          id: Date.now(),
          login: userData.login,
          nome: userData.nome,
          role: userData.role,
          email: userData.email,
          telefone: userData.telefone,
          isActive: true,
          createdAt: new Date().toISOString()
        }

        users.push(newUser)
        this.saveLocalUsers(users)

        return {
          success: true,
          data: newUser,
          message: 'Usuário criado localmente (erro na API)'
        }
      } catch (localError) {
        console.error('Erro ao criar usuário localmente:', localError)
        return {
          success: false,
          data: {} as UserWithPermissions,
          message: 'Erro ao criar usuário'
        }
      }
    }
  },

  // Atualizar usuário
  async updateUser(id: number, userData: UpdateUserData): Promise<UserResponse> {
    try {
      
      // Primeiro, tentar atualizar via API
      const apiResponse = await api.put(`/update-user-local/${id}`, userData)
      
      if (apiResponse.data && (apiResponse.data.success || apiResponse.data.sucess)) {
        const apiUser = apiResponse.data.data || apiResponse.data.user
        
        const formattedUser: UserWithPermissions = {
          id: apiUser.id,
          login: apiUser.login,
          nome: apiUser.nome || apiUser.login,
          role: apiUser.is_admin ? 'administrador' : (apiUser.role || 'usuario_basico'),
          is_admin: apiUser.is_admin,
          email: apiUser.email,
          telefone: apiUser.telefone,
          isActive: apiUser.isActive !== false,
          createdAt: apiUser.created_at || apiUser.createdAt,
          lastLogin: apiUser.lastLogin
        }
        
        return {
          success: true,
          data: formattedUser,
          message: 'Usuário atualizado com sucesso na API'
        }
      }
      
      // Fallback para atualização local
      const users = this.getLocalUsers()
      const userIndex = users.findIndex(u => u.id === id)
      
      if (userIndex === -1) {
        return {
          success: false,
          data: {} as UserWithPermissions,
          message: 'Usuário não encontrado'
        }
      }

      users[userIndex] = {
        ...users[userIndex],
        ...userData
      }

      this.saveLocalUsers(users)

      return {
        success: true,
        data: users[userIndex],
        message: 'Usuário atualizado localmente (API não disponível)'
      }
    } catch (error) {
      console.error('Erro ao atualizar usuário na API:', error)
      
      // Fallback para atualização local em caso de erro
      try {
        const users = this.getLocalUsers()
        const userIndex = users.findIndex(u => u.id === id)
        
        if (userIndex === -1) {
          return {
            success: false,
            data: {} as UserWithPermissions,
            message: 'Usuário não encontrado'
          }
        }

        users[userIndex] = {
          ...users[userIndex],
          ...userData
        }

        this.saveLocalUsers(users)

        return {
          success: true,
          data: users[userIndex],
          message: 'Usuário atualizado localmente (erro na API)'
        }
      } catch (localError) {
        console.error('Erro ao atualizar usuário localmente:', localError)
        return {
          success: false,
          data: {} as UserWithPermissions,
          message: 'Erro ao atualizar usuário'
        }
      }
    }
  },

  // Deletar usuário
  async deleteUser(id: number): Promise<{ success: boolean; message?: string }> {
    try {
      // Primeiro, tentar deletar via API
      const apiResponse = await api.delete(`/delete-user-local/${id}`)
      
      if (apiResponse.data && apiResponse.data.success) {
        return {
          success: true,
          message: 'Usuário removido com sucesso da API'
        }
      }
      
      // Fallback para remoção local
      console.warn('API não disponível, removendo usuário localmente')
      const users = this.getLocalUsers()
      const userIndex = users.findIndex(u => u.id === id)
      
      if (userIndex === -1) {
        return {
          success: false,
          message: 'Usuário não encontrado'
        }
      }

      users.splice(userIndex, 1)
      this.saveLocalUsers(users)

      return {
        success: true,
        message: 'Usuário removido localmente (API não disponível)'
      }
    } catch (error) {
      console.error('Erro ao deletar usuário na API:', error)
      
      // Fallback para remoção local em caso de erro
      try {
        const users = this.getLocalUsers()
        const userIndex = users.findIndex(u => u.id === id)
        
        if (userIndex === -1) {
          return {
            success: false,
            message: 'Usuário não encontrado'
          }
        }

        users.splice(userIndex, 1)
        this.saveLocalUsers(users)

        return {
          success: true,
          message: 'Usuário removido localmente (erro na API)'
        }
      } catch (localError) {
        console.error('Erro ao deletar usuário localmente:', localError)
        return {
          success: false,
          message: 'Erro ao deletar usuário'
        }
      }
    }
  },

  // Obter usuários do localStorage (simulação)
  getLocalUsers(): UserWithPermissions[] {
    try {
      const users = localStorage.getItem('portal_users')
      if (users) {
        return JSON.parse(users)
      }
      
      // Usuários padrão
      const defaultUsers: UserWithPermissions[] = [
        {
          id: 1,
          login: 'janiocesar',
          nome: 'Janio Cesar',
          role: 'administrador',
          isActive: true,
          createdAt: new Date().toISOString()
        },
        {
          id: 2,
          login: 'admin',
          nome: 'Administrador',
          role: 'administrador',
          isActive: true,
          createdAt: new Date().toISOString()
        },
        {
          id: 3,
          login: 'maria',
          nome: 'Maria Silva',
          role: 'vistoria',
          isActive: true,
          createdAt: new Date().toISOString()
        },
        {
          id: 4,
          login: 'joao',
          nome: 'João Santos',
          role: 'administrador',
          isActive: true,
          createdAt: new Date().toISOString()
        },
        {
          id: 5,
          login: 'ana',
          nome: 'Ana Costa',
          role: 'usuario_basico',
          isActive: true,
          createdAt: new Date().toISOString()
        }
      ]

      this.saveLocalUsers(defaultUsers)
      return defaultUsers
    } catch (error) {
      console.error('Erro ao obter usuários locais:', error)
      return []
    }
  },

  // Salvar usuários no localStorage
  saveLocalUsers(users: UserWithPermissions[]): void {
    try {
      localStorage.setItem('portal_users', JSON.stringify(users))
    } catch (error) {
      console.error('Erro ao salvar usuários:', error)
    }
  },

  // Sincronizar usuário da API para localStorage
  async syncUserToLocalStorage(id: number): Promise<UserWithPermissions | null> {
    try {
      const response = await this.getUserById(id)
      
      if (response.success) {
        const user = response.data
        const users = this.getLocalUsers()
        const existingIndex = users.findIndex(u => u.id === id)
        
        if (existingIndex >= 0) {
          // Atualizar usuário existente
          users[existingIndex] = user
        } else {
          // Adicionar novo usuário
          users.push(user)
        }
        
        this.saveLocalUsers(users)
        return user
      }
      
      return null
    } catch (error) {
      console.error('❌ syncUserToLocalStorage - Erro:', error)
      return null
    }
  },

  // Atualizar usuário apenas no localStorage (fallback)
  async updateUserLocalOnly(id: number, userData: UpdateUserData): Promise<UserResponse> {
    try {
      const users = this.getLocalUsers()
      const userIndex = users.findIndex(u => u.id === id)
      
      if (userIndex === -1) {
        
        // Tentar sincronizar da API primeiro
        const syncedUser = await this.syncUserToLocalStorage(id)
        if (syncedUser) {
          return this.updateUserLocalOnly(id, userData)
        }
        
        return {
          success: false,
          data: {} as UserWithPermissions,
          message: 'Usuário não encontrado no localStorage nem na API'
        }
      }

      // Atualizar apenas os campos fornecidos
      users[userIndex] = {
        ...users[userIndex],
        ...userData,
        is_admin: Array.isArray(userData.role) 
          ? userData.role.includes('administrador')
          : userData.role === 'administrador'
      }

      this.saveLocalUsers(users)

      return {
        success: true,
        data: users[userIndex],
        message: 'Usuário atualizado no localStorage'
      }
    } catch (error) {
      console.error('❌ updateUserLocalOnly - Erro:', error)
      return {
        success: false,
        data: {} as UserWithPermissions,
        message: 'Erro ao atualizar usuário no localStorage'
      }
    }
  },

  // Obter roles disponíveis
  getAvailableRoles(): UserRole[] {
    return ['administrador', 'vistoria', 'calendario', 'cardapio', 'usuario_basico']
  },

  // Validar dados do usuário
  validateUserData(userData: Partial<CreateUserData>): { isValid: boolean; errors: string[] } {
    const errors: string[] = []

    if (!userData.login || userData.login.trim().length < 3) {
      errors.push('Login deve ter pelo menos 3 caracteres')
    }

    if (!userData.nome || userData.nome.trim().length < 2) {
      errors.push('Nome deve ter pelo menos 2 caracteres')
    }

    if (userData.senha && userData.senha.length < 6) {
      errors.push('Senha deve ter pelo menos 6 caracteres')
    }

    if (userData.role) {
      const roles = Array.isArray(userData.role) ? userData.role : [userData.role]
      const availableRoles = this.getAvailableRoles()
      const invalidRoles = roles.filter(role => !availableRoles.includes(role))
      if (invalidRoles.length > 0) {
        errors.push(`Roles inválidos: ${invalidRoles.join(', ')}`)
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }
}
